import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ProgressBar } from "react-bootstrap";
import { RowContainer } from "../../../components/containers/styledComponents";
import { compassLightGreyBackground, greyText } from "../../../styles";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { CompassTag } from "../../../components/visuals/CompassTag";
import {
  CLIMATE_MATURITY_LEVELS,
  TPI_MANAGEMENT_SCORE_TO_BADGE_COLORS_MAPPING,
  TPIMetric,
  TPIScore,
} from "./constants";

import { PublicMetric } from "../../../models/CompanyprofileModels";
import { getMetricValue } from "./util";
import { isNotNan } from "../../../utils/formattingUtils";

const MAX_SCORE = 5;

const BarsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100px;
  padding: 0 30px;
  overflow-y: auto;
  align-items: center;
`;

const MilestoneProgressBar = styled(ProgressBar)`
  height: 14px;
  width: 85%;
  background-color: ${compassLightGreyBackground};
`;

const LabelProgressBar = styled(MilestoneProgressBar)<{ score: number }>`
  height: 50px;
  padding-left: ${({ score }): string => `${score * 10}px`};
  background-color: transparent;
`;

const TitleContainer = styled.div`
  width: 120px;
  overflow-x: auto;
`;

const BarLabel = styled.span`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: ${greyText};
  font-size: 12px;
`;

const ScoreProgressBar = styled(ProgressBar)<{ color: string }>`
  background-color: ${({ color }): string => color};
`;

const TransparentProgressBar = styled(ProgressBar)`
  background-color: transparent;
`;

const TitleText = styled(CompassParagraph)<{ isTransparent: boolean }>`
  color: ${({ isTransparent }): string =>
    isTransparent ? "transparent" : greyText};
  font-size: 12px;
  margin-right: 15px;
`;

const ProgressRow = styled(RowContainer)<{
  zIndex?: number;
  isFirst?: boolean;
}>`
  justify-content: flex-start;
  align-items: center;
  z-index: ${({ zIndex }): number => zIndex || 1};
  margin-top: ${({ isFirst }): string => (isFirst ? "0" : "-10px")};
  margin-bottom: ${({ isFirst }): string => (isFirst ? "-1px" : "0")};
`;

export const TPIManagementScoreBar = ({
  metricsData,
}: {
  metricsData: PublicMetric[];
}): JSX.Element => {
  const [managementQualityScore, setManagementQualityScore] =
    useState<TPIScore>(0);
  const [colors, setColors] = useState<{
    bg: string;
    text: string;
  }>(TPI_MANAGEMENT_SCORE_TO_BADGE_COLORS_MAPPING[0]);

  useEffect(() => {
    const rawTpiScore: string = getMetricValue(
      TPIMetric.TPI_MQ_Score,
      metricsData
    ) as string;

    if (isNotNan(rawTpiScore)) {
      const tpiScore = parseInt(rawTpiScore[0]) as TPIScore;
      setManagementQualityScore(tpiScore);
      setColors(TPI_MANAGEMENT_SCORE_TO_BADGE_COLORS_MAPPING[tpiScore]);
    }
  }, [metricsData]);

  return (
    <BarsContainer>
      {/* Score Progress Bar */}
      <ProgressRow zIndex={2} isFirst>
        <TitleContainer>
          <TitleText isTransparent={false}>Management Quality Score</TitleText>
        </TitleContainer>
        <MilestoneProgressBar>
          <ScoreProgressBar
            color={colors.text}
            now={managementQualityScore}
            min={0}
            max={MAX_SCORE}
          />
        </MilestoneProgressBar>
      </ProgressRow>

      {/* Transparent Labels Bar */}
      <ProgressRow>
        <TitleContainer>
          <TitleText isTransparent>Management Quality Score</TitleText>
        </TitleContainer>
        <LabelProgressBar
          score={managementQualityScore}
          now={managementQualityScore}
          min={0}
          max={MAX_SCORE}
        >
          {Array.from({ length: MAX_SCORE }, (_, index) => index + 1).map(
            (step) => (
              <TransparentProgressBar
                min={0}
                max={MAX_SCORE}
                variant="custom"
                now={managementQualityScore}
                key={step}
                label={
                  step === managementQualityScore && (
                    <BarLabel>
                      <CompassParagraph color={greyText} fontSize={12}>
                        {CLIMATE_MATURITY_LEVELS[step]}
                      </CompassParagraph>
                      <CompassTag
                        tagKey={step.toString()}
                        color={colors.text}
                        bg={colors.bg}
                        displayValue={step.toString()}
                        hideMargin
                      />
                    </BarLabel>
                  )
                }
              />
            )
          )}
        </LabelProgressBar>
      </ProgressRow>
    </BarsContainer>
  );
};
